import {
  canCancelRealEstateRequest,
  getRealEstateNextStepCopy,
  mapInspection,
} from "./investorRealEstateAdapter";
import { SERVICE_REQUEST_STATUS, SERVICE_REQUEST_TYPE } from "../store/admin/investorOperations/constants";
import { getRecommendedServiceRequestAction } from "../store/admin/investorOperations/serviceRequestLifecycle";
import { normalizeTimelineItems } from "./timeline";

export function mapStaffRealEstateTimeline(entries = []) {
  return normalizeTimelineItems(entries);
}

export function mapStaffRealEstateNotes(notes = []) {
  return (notes || []).map((n) => ({
    id: n.id,
    text: n.text || n.note || "",
    staffName: n.staff_name || null,
    at: n.at || null,
    isInternal: n.is_internal !== false,
  }));
}

export function mapStaffRealEstateListItem(raw) {
  if (!raw) return null;
  const re = raw.real_estate || {};
  const inspection = mapInspection(raw.inspection);

  return {
    id: raw.id,
    type: SERVICE_REQUEST_TYPE.REAL_ESTATE,
    requestReference: raw.request_reference,
    reference: raw.request_reference,
    accountType: raw.account_type,
    customerType: raw.customer_type,
    applicantId: raw.applicant_id,
    customerName: raw.customer_name,
    customerEmail: raw.customer_email,
    customerPhone: raw.customer_phone,
    title: raw.title || re.property_name || "Property request",
    estimatedValueNgn: raw.estimated_value_ngn != null ? Number(raw.estimated_value_ngn) : null,
    status: raw.status,
    assignedStaffId: raw.assigned_staff_id ?? null,
    assignedStaffName: raw.assigned_staff_name || null,
    preferredContact: raw.preferred_contact || null,
    submittedAt: raw.submitted_at || null,
    updatedAt: raw.updated_at || null,
    realEstate: {
      propertyId: re.property_id || null,
      propertyName: re.property_name || raw.title,
      location: re.location || null,
      unitsRequested: re.units_requested ?? null,
      inspectionPreference: re.inspection_preference || null,
      documentationInterest: Boolean(re.documentation_interest),
      requestNote: re.request_note || null,
    },
    inspection,
    inspectionDate: inspection?.date || null,
  };
}

export function mapStaffRealEstateDetail(raw) {
  if (!raw) return null;
  const base = mapStaffRealEstateListItem(raw);
  if (!base) return null;

  const recommended = getRecommendedServiceRequestAction(SERVICE_REQUEST_TYPE.REAL_ESTATE, raw.status);

  return {
    ...base,
    customerNote: raw.customer_note || null,
    declineReason: raw.decline_reason || null,
    cancelReason: raw.cancel_reason || null,
    reviewedBy: raw.reviewed_by || null,
    reviewedAt: raw.reviewed_at || null,
    contactedAt: raw.contacted_at || null,
    approvedAt: raw.approved_at || null,
    completedAt: raw.completed_at || null,
    inspections: (raw.inspections || []).map(mapInspection).filter(Boolean),
    staffNotes: mapStaffRealEstateNotes(raw.staff_notes),
    timeline: mapStaffRealEstateTimeline(raw.timeline),
    recommendedAction: recommended,
    nextStep: getRealEstateNextStepCopy(raw.status),
    isTerminal: [
      SERVICE_REQUEST_STATUS.COMPLETED,
      SERVICE_REQUEST_STATUS.DECLINED,
      SERVICE_REQUEST_STATUS.CANCELLED,
    ].includes(raw.status),
    canCancelApplicantStyle: canCancelRealEstateRequest(raw.status),
  };
}

export function mapStaffRealEstateSummary(raw) {
  if (!raw) return null;
  const byStatus = raw.by_status || {};
  return {
    totalOpen: raw.total_open ?? 0,
    inspectionsToSchedule: raw.inspections_to_schedule ?? 0,
    inspectionScheduled: raw.inspection_scheduled ?? 0,
    assignedToMe: raw.assigned_to_me ?? 0,
    overdue: raw.overdue ?? 0,
    completedThisMonth: raw.completed_this_month ?? 0,
    byStatus,
    submitted: byStatus.submitted ?? 0,
    awaitingContact: byStatus.awaiting_contact ?? 0,
    awaitingCustomer: byStatus.awaiting_customer ?? 0,
    approved: byStatus.approved ?? 0,
    completed: byStatus.completed ?? 0,
    declined: byStatus.declined ?? 0,
    cancelled: byStatus.cancelled ?? 0,
    upcomingInspections: (raw.upcoming_inspections || []).map((i) => ({
      id: i.id,
      requestId: i.request_id,
      requestReference: i.request_reference,
      date: i.date,
      time: i.time,
      location: i.location,
      assignedStaffName: i.assigned_staff_name,
      status: i.status,
    })),
    recentActivity: (raw.recent_activity || []).map(mapStaffRealEstateListItem).filter(Boolean),
  };
}

export function buildScheduleInspectionPayload(values) {
  const staffId = values.assignedStaffId ?? values.assigned_staff_id;
  const parsedId = staffId != null && staffId !== "" ? Number(staffId) : undefined;

  return {
    date: values.date,
    time: values.time,
    location: values.location,
    assigned_staff_id: Number.isFinite(parsedId) && parsedId > 0 ? parsedId : undefined,
    assigned_staff_name: values.assignedStaffName || values.assigned_staff_name || undefined,
    note: values.note || undefined,
  };
}

/** Map UI inspection focus onto request status (no inspection_status API filter). */
export function mapInspectionFocusToRequestStatus(focus) {
  if (focus === "scheduled") return SERVICE_REQUEST_STATUS.INSPECTION_SCHEDULED;
  if (focus === "completed") return SERVICE_REQUEST_STATUS.INSPECTION_COMPLETED;
  return null;
}
